import { createStore, select, type Store, withProps } from '@ngneat/elf';
import { localStorageStrategy, persistState } from '@ngneat/elf-persist-state';
import { isNil } from 'lodash-es';
import { map, type Observable, shareReplay, take } from 'rxjs';
import { filter } from 'rxjs/operators';

import type {
  IEntityLookupBean,
  ServiceOffering,
  TermAndCondition,
  VerifyOutputUser,
} from '@core/models/app.model';
import type { IUserSession } from '@core/models/user.model';
import type { UmeService } from '@core/services/ume-services';
import { APP_ROLE, type AppRole } from '@shared/constants/ume-constants';

const _disabledInviteMessage = {
  disabledByVP:
    'This option is currently disabled for your account.' +
    ' Please contact an admin of the virtual premium account to enable it.',
} as const;

export interface AuthProps {
  verifyInfo: VerifyOutputUser;
  sessionUser: IUserSession;
  isLoggedIn: boolean;
  companyTerms: TermAndCondition[];
  disabledVerificationRequestUntil: Record<string, number | null>;
}

const AUTH_STORE: Store<{ name: string; state: AuthProps; config: undefined }, AuthProps> =
  createStore(
    { name: 'auth' },
    withProps<AuthProps>({
      verifyInfo: {} as VerifyOutputUser,
      sessionUser: {} as IUserSession,
      isLoggedIn: false,
      companyTerms: [],
      disabledVerificationRequestUntil: {},
    })
  );

persistState(AUTH_STORE, {
  key: 'auth-timer',
  storage: localStorageStrategy,
});

/**
 * Store observables
 */

export const verifyInfo$: Observable<VerifyOutputUser> = AUTH_STORE.pipe(
  select((state) => state.verifyInfo),
  shareReplay({ refCount: true })
);

export const sessionUser$: Observable<IUserSession> = AUTH_STORE.pipe(
  select((state) => state.sessionUser),
  shareReplay({ refCount: true })
);

export const isLoggedIn$: Observable<boolean> = AUTH_STORE.pipe(
  select((state) => Boolean(state.sessionUser.selectedCompany)),
  shareReplay({ refCount: true })
);

export const companyTerms$: Observable<TermAndCondition[]> = AUTH_STORE.pipe(
  select((state) => state.companyTerms),
  shareReplay({ refCount: true })
);

export const sessionUserLinkedCompanies$: Observable<IEntityLookupBean[]> = AUTH_STORE.pipe(
  select((state) => state.sessionUser.linkedCompanies),
  shareReplay({ refCount: true })
);

export const userSelectedCompany$: Observable<IEntityLookupBean> = AUTH_STORE.pipe(
  select((state) => state.sessionUser.selectedCompany)
);

export const counterpartyManagingDisabledForSelectedCompany$: Observable<string | null> =
  AUTH_STORE.pipe(
    filter((state) => !isNil(state?.sessionUser?.selectedCompany?.counterpartyManagementEnabled)),
    select((state) => state.sessionUser.selectedCompany?.counterpartyManagementEnabled),
    map((enabled) => (enabled ? null : _disabledInviteMessage.disabledByVP))
  );

export const selectDisabledUntilForEmail$ = (email: string): Observable<number | null> =>
  AUTH_STORE.pipe(select((state) => state.disabledVerificationRequestUntil?.[email] || null));

export const isButtonDisabledForEmail$ = (email: string): Observable<boolean> =>
  selectDisabledUntilForEmail$(email).pipe(
    map((disabledUntil) => disabledUntil !== null && disabledUntil > Date.now())
  );

/**
 * Storing operations with state
 */

export function setVerifyInfo(verifyInfo: AuthProps['verifyInfo']): void {
  AUTH_STORE.update((state: AuthProps) => ({
    ...state,
    verifyInfo,
  }));
}

export function setSessionUser(sessionUser: AuthProps['sessionUser']): void {
  AUTH_STORE.update((state: AuthProps) => ({
    ...state,
    sessionUser,
  }));
}

export function setCompanyTerms(companyTerms: AuthProps['companyTerms']): void {
  AUTH_STORE.update((state: AuthProps) => ({
    ...state,
    companyTerms,
  }));
}

export function setIsLoggedIn(isLoggedIn: AuthProps['isLoggedIn']): void {
  AUTH_STORE.update((state: AuthProps) => ({
    ...state,
    isLoggedIn,
  }));
}

export function setDisableVerificationRequest(email: string, disableUntil: number | null): void {
  AUTH_STORE.update((state: AuthProps) => ({
    ...state,
    disabledVerificationRequestUntil: {
      ...state.disabledVerificationRequestUntil,
      [email]: disableUntil,
    },
  }));
}

export function resetAuthStore(): void {
  AUTH_STORE.reset();
}

export function getSessionUser(): IUserSession {
  return AUTH_STORE.getValue().sessionUser;
}

export function getUserSelectedCompany(): IEntityLookupBean {
  return AUTH_STORE.getValue().sessionUser.selectedCompany as IEntityLookupBean;
}

export function isLoggedInUser(): boolean {
  return Boolean(AUTH_STORE.getValue().sessionUser?.selectedCompany);
}

export function hasService(serviceName: UmeService): Observable<boolean> {
  return userSelectedCompany$.pipe(
    take(1),
    map(({ services }: IEntityLookupBean) => {
      return Boolean(services).valueOf() && services.includes(serviceName);
    })
  );
}

export function hasVirtualService(serviceName: UmeService): Observable<boolean> {
  return userSelectedCompany$.pipe(
    take(1),
    map(({ virtualPremiumServices }: IEntityLookupBean) => {
      return (
        Boolean(virtualPremiumServices).valueOf() && virtualPremiumServices.includes(serviceName)
      );
    })
  );
}

export function hasServiceSync(serviceName: UmeService): boolean {
  return getUserSelectedCompany()?.services?.includes(serviceName);
}

export function hasVirtualServiceSync(serviceName: UmeService): boolean {
  return getUserSelectedCompany()?.virtualPremiumServices?.includes(serviceName);
}

export function hasOfferingSync(offering: ServiceOffering): boolean {
  return getUserSelectedCompany()?.serviceOfferings?.includes(offering);
}

export function hasApplicationRole(roleName: AppRole | string): boolean {
  const res = getUserSelectedCompany()?.applicationRoles?.find(
    (role: AppRole) => role === roleName || role === APP_ROLE.ADMIN
  );
  return !!res;
}

export function getCompanyTerms(): TermAndCondition[] {
  return AUTH_STORE.getValue().companyTerms;
}

export function hasOffering(
  searchableOffering: ServiceOffering,
  offerings: ServiceOffering[]
): boolean {
  return _someOfOfferings(searchableOffering, offerings);
}

/*
 * Check functions
 */

export function _someOfOfferings(
  searchable: ServiceOffering,
  offerings: ServiceOffering[]
): boolean {
  return offerings.some((off) => off === searchable);
}
